// Stufenbranches: Abbildung der Board-Antwort (GET /api/cicd/transport/stage-board) auf die Props der
// unveränderten PromotionStageBoard (Vertrag 6.4) und Release-Vorauswahl (Vertrag 10.1).
// Reine Funktionen ohne Zustand: Kandidaten-IDs, Zustände und Nachweise kommen ausschließlich vom Server.

export const NO_TOPIC = "__none__";

const PROMOTABLE = new Set(["reached", "partial", "deployment-failed"]);
const WITH_EXECUTION = new Set(["running", "failed", "partial", "deployment-failed"]);
// Offene Antragszustände wie OPEN_REQUEST_STATUS im Vertrag (5, 6.4 Regel 4).
const OPEN_REQUEST = new Set(["pending", "queued", "running", "unknown"]);

const configuredStages = board => (board?.stages || []).filter(stage => stage.configured === true);
const stageOf = (board, key) => (board?.stages || []).find(stage => stage.key === key) || null;

const matchesTopic = (pkg, topicId) => topicId === undefined || topicId === null ? true
  : topicId === NO_TOPIC ? pkg.topicId === null || pkg.topicId === undefined
    : pkg.topicId === topicId;

/** Nächste konfigurierte Stufe nach `key`, `null` für die letzte oder eine unbekannte Stufe. */
export function nextStage(board, key) {
  const keys = configuredStages(board).map(stage => stage.key);
  const index = keys.indexOf(key);
  return index < 0 ? null : keys[index + 1] || null;
}

// --- Mehrere Ziele je Stufe (Vertrag V.3) -------------------------------------------------------------------------
// Ohne `board.multiTarget` bleibt alles wie bisher. Im Mehrzielmodus liefert der Server je Stufe `targets` und je Paket
// `targets[stage][targetId]` (Zelle des Ziels); `cells[stage]` ist das Standardziel.

/** Regionen aller Ziele des Boards, sortiert; leer ohne Mehrzielmodus. */
export function regionsOf(board) {
  if (!board?.multiTarget) return [];
  const regions = new Set();
  for (const stage of board.stages || []) for (const target of stage.targets || []) if (target.region) regions.add(target.region);
  return [...regions].sort((a, b) => a.localeCompare(b));
}

const defaultTargetOf = targets => targets.find(target => target.isDefault) || targets[0];

// Gezeigte Ziele einer Stufe: ohne Region alle, mit Region die der Region; eine Stufe ohne Ziel der Region zeigt ihr Standardziel.
function shownTargets(stage, region) {
  const all = stage.targets || [];
  if (!all.length) return null;
  if (region === undefined || region === null || region === "") return all;
  const inRegion = all.filter(target => target.region === region);
  return inRegion.length ? inRegion : [defaultTargetOf(all)];
}

/**
 * Board auf eine Region zugeschnitten: Zellen und Instanz einer Stufe stammen aus den Zielen der Region. Zeigt eine Stufe
 * genau ein Ziel, hat das Ergebnis die Form eines Boards ohne Mehrzielmodus (gleiche Props wie heute). Bei mehreren
 * gezeigten Zielen bleiben `targets` an Stufe und Paket für die Untergruppen erhalten; `allTargets` nennt alle Ziele der Stufe.
 */
export function projectRegion(board, region) {
  if (!board?.multiTarget) return board;
  const shown = new Map();
  const stages = (board.stages || []).map(stage => {
    const list = shownTargets(stage, region);
    if (!list) return stage;
    shown.set(stage.key, list);
    const { targets, ...rest } = stage;
    if (list.length > 1) return { ...rest, targets: list, allTargets: targets };
    return { ...rest, instanceId: list[0].instanceId, instanceName: list[0].instanceName, allTargets: targets };
  });
  const packages = (board.packages || []).map(pkg => {
    const cells = { ...pkg.cells }, targets = {};
    for (const [key, list] of shown) {
      const byTarget = pkg.targets?.[key];
      if (list.length > 1) {
        targets[key] = Object.fromEntries(list.filter(target => byTarget?.[target.targetId]).map(target => [target.targetId, byTarget[target.targetId]]));
      } else if (byTarget?.[list[0].targetId]) {
        const { targetId, ...cell } = byTarget[list[0].targetId];
        cells[key] = cell;
      }
    }
    // `targetCells` hält alle Zielzellen, damit die Beantragbarkeit nicht von der Region abhängt.
    const { targets: allCells, ...rest } = pkg;
    return { ...rest, cells, ...(Object.keys(targets).length ? { targets } : {}), ...(allCells ? { targetCells: allCells } : {}) };
  });
  return { ...board, stages, packages };
}

// Zellen einer Stufe, je gezeigtem Ziel eine (mit `target`), sonst die eine Zelle der Stufe.
function cellsOf(board, pkg, key) {
  const stage = stageOf(board, key);
  if (stage?.targets?.length > 1) return stage.targets.map(target => ({ target, cell: pkg.targets?.[key]?.[target.targetId] })).filter(entry => entry.cell);
  return pkg.cells?.[key] ? [{ target: null, cell: pkg.cells[key] }] : [];
}

const contentAt = cell => cell.proof?.completedAt ?? cell.contentAt;

// Ein-Karten-Regel (Entscheidung David 27.10.2026): `pkg.card.stage` vom Server ist die Stufe der einen Karte des Pakets, der
// neueste eigene Stand an seiner vordersten Stufe (board.js cardOf, nur eigene Ereignisse). Ältere Stände erscheinen nie.
// Eine Antwort ohne `card` (ältere Fixtures) zeigt wie DEV177 jede Zelle, die keine spätere Stufe mit gleichem Stand verdeckt.
// Pakettransport (Welle 6): `pkg.transport` vom Server, nur "package" wird weitergereicht. Fehlt es, bleibt alles wie bisher.
const transportOf = pkg => (pkg?.transport === "package" ? { transport: "package" } : {});

// DEV183 I4: nicht sperrende Hinweise je fremder Referenz (Lane C, pkg.referenceHints), an der Karte, deren Folgestufe
// die targetStage des Hinweises ist; Text vom Server.
const referenceHintsOf = (pkg, targetStage) => (Array.isArray(pkg?.referenceHints) ? pkg.referenceHints : [])
  .filter(hint => hint && typeof hint.message === "string" && hint.message.trim() && (!hint.targetStage || hint.targetStage === targetStage))
  .map(hint => ({ id: hint.id || hint.referenceId || hint.message, message: hint.message.trim(), ...(hint.code ? { code: hint.code } : {}) }));

const cardStageOf = pkg => (pkg.card && typeof pkg.card.stage === "string" ? pkg.card.stage : null);

function evidenceOf(pkg, cell, extra) {
  const proof = cell.proof;
  if (!proof?.runId) return [];
  return [{ runId: proof.runId, sourceSha: proof.commit, at: proof.completedAt ?? null, artifactIds: [...(pkg.artifactIds || [])], ...extra }];
}

function stagePackage(pkg, cell, target) {
  return { packageId: pkg.packageId, packageName: pkg.packageName, artifactIds: [...(pkg.artifactIds || [])], at: contentAt(cell),
    runId: cell.proof?.runId ?? null, sourceSha: cell.commit, kind: "transport", stageState: cell.state, evidence: evidenceOf(pkg, cell, {}),
    ...(target ? { targetId: target.targetId } : {}), ...transportOf(pkg) };
}

// Erste zutreffende Regel aus Vertrag 6.4; nur mit Zielstufe. Ein fehlender grüner Nachweis der Quellstufe sperrt nicht
// mehr (Entscheidung David 27.10.2026: Paket- und Stufenrichtigkeit prüft allein das GitHub-Repository).
// `targetIds`: gezeigte Ziele der Zielstufe (Mehrzielmodus). Ein offener Antrag sperrt nur, wenn er alle diese Ziele abdeckt;
// ältere Anträge ohne `targets` gelten als ["default"].
function blockedReasonOf(pkg, cell, sourceStage, targetStage, sameAsNext, targetIds) {
  if (!targetStage) return undefined;
  if (pkg.freeze?.frozen) return "PACKAGE_FROZEN";
  if (targetStage === "prod" && pkg.prodLock?.locked) return "STAGE_PROD_LOCKED";
  // Veraltete Anträge (card.outdated) ersetzt der Server, sobald ein neuer Antrag kommt; sie sperren die Karte nicht.
  const outdated = new Set(pkg.card?.outdated || []);
  const open = (pkg.openRequests || []).filter(request => request.sourceStage === sourceStage
    && request.targetStage === targetStage && OPEN_REQUEST.has(request.status) && !outdated.has(request.requestId));
  if (!sameAsNext && open.length && (!targetIds || targetIds.every(id => open.some(request => (request.targets || ["default"]).includes(id))))) return "REQUEST_ALREADY_OPEN";
  return undefined;
}

function candidateOf(board, pkg, sourceStage, cell = pkg.cells[sourceStage], target = null) {
  const rights = board.rights || {};
  const targetStage = nextStage(board, sourceStage);
  // Alle Zielzellen der nächsten Stufe, unabhängig von der gewählten Region.
  const targetCells = !targetStage ? [] : pkg.targetCells?.[targetStage] ? Object.values(pkg.targetCells[targetStage])
    : cellsOf(board, pkg, targetStage).map(entry => entry.cell);
  // Gleicher Stand wie die nächste Stufe heißt: alle gezeigten Ziele der nächsten Stufe haben ihn.
  const sameAsNext = targetCells.length > 0 && targetCells.every(targetCell => targetCell.scopeDigest === cell.scopeDigest);
  const allTargetsOfNext = targetStage ? stageOf(board, targetStage)?.allTargets || stageOf(board, targetStage)?.targets : null;
  const targetIds = allTargetsOfNext?.length > 1 ? allTargetsOfNext.map(item => item.targetId) : null;
  const blockedReason = blockedReasonOf(pkg, cell, sourceStage, targetStage, sameAsNext, targetIds);
  // Gleicher Stand wie die nächste Stufe sperrt nicht mehr (Entscheidung David 27.10.2026): erneutes Ausrollen ist erlaubt,
  // sameAsNext bleibt als Hinweis an der Karte. Es sperren nur Rechte und die bewusst gesetzten Sperren (blockedReason).
  const selectable = targetStage !== null && !blockedReason && rights.canRequest === true
    && (rights.requestStages || []).includes(sourceStage);
  const candidate = {
    id: cell.candidateId, packageId: pkg.packageId, packageName: pkg.packageName, sourceStage, targetStage,
    // DEV183 A: Auf DEV zählt die Zeit des eigenen Stands (pkg.card.contentAt), nicht eine fremde Änderung an einer Referenz.
    sourceSha: cell.commit, at: (sourceStage === "dev" && pkg.card?.stage === "dev" && pkg.card.contentAt) || contentAt(cell), artifactIds: [...(pkg.artifactIds || [])],
    evidence: evidenceOf(pkg, cell, { ok: cell.state === "reached" }), stageState: cell.state, selectable, sameAsNext,
    freeze: pkg.freeze, prodLock: pkg.prodLock, topicId: pkg.topicId ?? null,
    ...(pkg.progressRequest ? { progressRequest: pkg.progressRequest } : {}),
    partnerDirectory: cell.partnerDirectory ?? null,
    // Topic-Sperre des Pakets (Vertrag 26.3), nur wenn das Board sie liefert.
    ...(pkg.topicLock !== undefined ? { topicLock: pkg.topicLock } : {}),
    sourceInstanceName: target ? target.instanceName : stageOf(board, sourceStage)?.instanceName ?? null,
    targetInstanceName: targetStage ? stageOf(board, targetStage)?.instanceName ?? null : null,
    ...(target ? { targetId: target.targetId, targetName: target.instanceName, region: target.region ?? null } : {}),
    ...transportOf(pkg),
    // DEV183: Hinweise zu fremden Referenzen für die Folgestufe dieser Karte, nur wenn geliefert.
    ...(referenceHintsOf(pkg, targetStage).length ? { referenceHints: referenceHintsOf(pkg, targetStage) } : {}),
  };
  if (blockedReason) candidate.blockedReason = blockedReason;
  // Warnung statt Sperre: Für das Ziel dieser Karte stehen im Stand keine eigenen Werte (Werte der Basisstufe).
  if (cell.valuesMissing === true) candidate.valuesMissing = true;
  // Cloud-Lauf ohne Edge-Nachlauf (Korrekturrunde 1 I7): Hinweis an der Karte.
  if (cell.edgeSkipped) candidate.edgeSkipped = { ...cell.edgeSkipped };
  if (WITH_EXECUTION.has(cell.state)) candidate.execution = { status: cell.state, requestId: cell.requestId ?? null,
    runId: cell.proof?.runId ?? null, runUrl: cell.proof?.runUrl ?? null, edge: cell.proof?.edge ?? null,
    runAttempt: cell.proof?.runAttempt ?? 1,
    ...(cell.proof?.edgeReason ? { edgeReason: cell.proof.edgeReason } : {}),
    ...(cell.proof?.uploadConfirmed === true ? { uploadConfirmed: true, importComplete: cell.proof.importComplete,
      importedArtifactIds: [...(cell.proof.importedArtifactIds || [])],
      importFailures: (cell.proof.importFailures || []).map(row => ({ ...row })) } : {}) };
  if (cell.state === "deployment-failed" && cell.proof?.uploadConfirmed === true) {
    candidate.deploymentFailures = (cell.deploymentFailures || cell.proof.deploymentFailures || []).map(row => ({ ...row }));
  }
  // Ein-Karten-Regel: gescheiterter Antrag in die Folgestufe (die Karte bleibt hier) und offene Anträge älterer Stände.
  if (pkg.card?.forward) candidate.forward = { ...pkg.card.forward };
  if (pkg.card?.olderRequests?.length) candidate.olderRequests = pkg.card.olderRequests.map(request => ({ ...request }));
  if (cell.state === "partial") {
    candidate.edgePending = ["pending", "failed"].includes(cell.proof?.edge) || !!cell.edgeSync;
    if (cell.edgeSync) candidate.edgeSync = { ...cell.edgeSync, partnerIds: [...cell.edgeSync.partnerIds],
      // Ohne PIDs (etwa bei nicht bewertbarer .stages.json, fail closed) gibt es nichts zu synchronisieren (Vertrag 6.4).
      canSync: cell.edgeSync.state === "failed" && cell.edgeSync.partnerIds.length > 0 && (rights.edgeSyncStages || []).includes(sourceStage) };
  }
  return candidate;
}

/**
 * Board → `{ includeDev, stages, candidates }` für PromotionStageBoard (Vertrag 6.4).
 * `region` (nur Mehrzielmodus): zeigt die Ziele dieser Region, ohne Region („Alle Regionen“) Untergruppen je Ziel.
 */
export function toStageBoardProps(rawBoard, { includeDev = false, topicId, region } = {}) {
  const board = projectRegion(rawBoard, region);
  // Archivierte Karten (board.archive, nur Webapp) erscheinen weder in der Spalte noch in ihrer Zählung. Die Regel
  // „spätere Stufe mit gleichem Stand blendet die frühere aus“ liest weiter die Zellen selbst, sonst käme nach dem
  // Archivieren die überholte PRE-PROD-Karte zurück.
  const archivedIds = new Set((board?.archive || []).map(entry => entry.candidateId));
  // Archiviert ist eine Zelle, wenn ihre Identität oder eine bisherige (legacyCandidateIds, Umfang wie DEV177) im Archiv steht.
  const isArchived = cell => archivedIds.has(cell.candidateId) || (cell.legacyCandidateIds || []).some(id => archivedIds.has(id));
  const packages = (board?.packages || []).filter(pkg => matchesTopic(pkg, topicId));
  const stages = (board?.stages || []).map(stage => {
    const row = { key: stage.key, configured: stage.configured, instanceName: stage.instanceName ?? null,
      packages: packages.flatMap(pkg => cellsOf(board, pkg, stage.key)
        .filter(({ cell }) => PROMOTABLE.has(cell.state) && !isArchived(cell))
        .map(({ cell, target }) => stagePackage(pkg, cell, target))) };
    if (stage.error) row.error = stage.error;
    if (stage.targets?.length > 1) row.targets = stage.targets.map(target => ({ ...target }));
    return row;
  });
  const keys = configuredStages(board).map(stage => stage.key).filter(key => includeDev || key !== "dev");
  const shownCells = (pkg, key) => cellsOf(board, pkg, key).filter(({ cell }) => !isArchived(cell) && cell.state !== "absent");
  // Mit `card` genau die Zellen der Kartenstufe (im Mehrzielmodus je gezeigtem Ziel als Teilstatus derselben Karte).
  const candidates = packages.flatMap(pkg => (cardStageOf(pkg) !== null
    ? (keys.includes(cardStageOf(pkg)) ? withRollout(board, pkg, cardStageOf(pkg), shownCells(pkg, cardStageOf(pkg))
      .map(({ cell, target }) => candidateOf(board, pkg, cardStageOf(pkg), cell, target))) : [])
    : keys.flatMap(key => shownCells(pkg, key)
      .filter(({ cell }) => !keys.slice(keys.indexOf(key) + 1).some(later => {
        const laterCells = cellsOf(board, pkg, later).map(entry => entry.cell);
        return cell.scopeDigest && laterCells.length > 0 && laterCells.every(item => item.scopeDigest === cell.scopeDigest && PROMOTABLE.has(item.state));
      }))
      .map(({ cell, target }) => candidateOf(board, pkg, key, cell, target)))));
  return { includeDev, stages, candidates };
}

// Mehrzielmodus: Ziele der Kartenstufe ohne den Stand (weder erreicht, teilweise noch laufend) bekommen ihn über einen Antrag
// aus der Vorstufe (Nachziehen). Die Karte bleibt auf ihrer Stufe; `rollout` nennt den Weg und steht an den Zielkarten ohne Stand
// (zeigt die Region keine davon, an der ersten gezeigten). Ohne Zusatzziele gibt es kein `rollout`.
function withRollout(board, pkg, stage, rows) {
  const keys = configuredStages(board).map(item => item.key);
  const previous = keys[keys.indexOf(stage) - 1];
  const all = pkg.targetCells?.[stage] || pkg.targets?.[stage];
  if (!board?.multiTarget || !previous || !rows.length || !all || Object.keys(all).length < 2) return rows;
  const missing = Object.entries(all).filter(([, cell]) => !["reached", "partial", "running", "deployment-failed"].includes(cell?.state)).map(([id]) => id);
  if (!missing.length) return rows;
  const rights = board.rights || {};
  const rollout = { sourceStage: previous, targetStage: stage, targetIds: missing,
    selectable: rights.canRequest === true && (rights.requestStages || []).includes(previous) && !pkg.freeze?.frozen && !(stage === "prod" && pkg.prodLock?.locked) };
  const own = rows.filter(row => missing.includes(row.targetId ?? "default"));
  for (const row of own.length ? own : rows.slice(0, 1)) row.rollout = { ...rollout, targetIds: [...missing] };
  return rows;
}

/** Anzahl auswählbarer und aller Kandidaten einer Quellspalte (Spaltenknopf, Vertrag 6.4). */
export function columnSelection(candidates, stageKey) {
  const rows = (candidates || []).filter(candidate => candidate.sourceStage === stageKey);
  return { selectable: rows.filter(candidate => candidate.selectable).length, total: rows.length };
}

/**
 * Release-Vorauswahl nach PROD (Vertrag 10.1 und 28, gleiche Regel wie release.preselect): betrachtet werden Pakete, deren
 * Zelle auf der Vorstufe von PROD vorhanden ist. Freeze und PROD-Sperre schließen aus (`excluded`). Alle anderen stehen in
 * `listed` und sind wählbar, auch ein Stand, der schon in PROD liegt (`sameAsProd`, erneutes Ausrollen) und ein roter oder
 * laufender Stand. Vorausgewählt (`packageIds`) sind nur `reached` und `partial` mit anderem Stand als PROD. Ein-Karten-Regel:
 * steht die Karte vor der Vorstufe, ist deren Stand veraltet und wird nicht angeboten (`excluded`, `STAGE_NEWER_STATE` mit `stage`);
 * steht sie auf PROD, ist der neueste Stand dort angekommen (`sameAsProd`).
 */
export function preselectRelease(board, { topicId } = {}) {
  const keys = configuredStages(board).map(stage => stage.key);
  const prodIndex = keys.indexOf("prod");
  const previous = prodIndex > 0 ? keys[prodIndex - 1] : null;
  const packageIds = [], excluded = [], listed = [];
  if (!previous) return { packageIds, excluded, listed };
  for (const pkg of board.packages || []) {
    if (!matchesTopic(pkg, topicId)) continue;
    // Mehrzielmodus: Die Vorstufe zählt, sobald irgendein Ziel grün ist; PROD ist fertig, wenn alle Ziele den Stand haben.
    const previousCells = Object.values(pkg.targets?.[previous] || {});
    const cell = previousCells.length ? previousCells.find(item => item.state === "reached") || previousCells.find(item => item.state === "partial") || previousCells[0]
      : pkg.cells?.[previous];
    if (!cell || cell.state === "absent") continue;
    const prodCells = Object.values(pkg.targets?.prod || {});
    const card = cardStageOf(pkg);
    const sameAs = item => item.scopeDigest === cell.scopeDigest;
    // Mehrzielmodus: Steht die Karte auf PROD, gilt ein Ziel als fertig, wenn es den Stand hat oder ihn erreicht hat; ein Ziel ohne
    // ihn lässt den Release für das Nachziehen vorausgewählt. Ohne Zusatzziele genügt die Karte auf PROD.
    const done = item => sameAs(item) || (card === "prod" && PROMOTABLE.has(item.state));
    const sameAsProd = prodCells.length ? prodCells.every(done) : card === "prod" || (!!pkg.cells?.prod && sameAs(pkg.cells.prod));
    const newerStage = card && keys.includes(card) && keys.indexOf(card) < keys.indexOf(previous) ? card : null;
    const reason = pkg.freeze?.frozen ? "PACKAGE_FROZEN" : pkg.prodLock?.locked ? "STAGE_PROD_LOCKED" : null;
    if (reason) { if (!sameAsProd) excluded.push({ packageId: pkg.packageId, reason }); continue; }
    if (newerStage) { excluded.push({ packageId: pkg.packageId, reason: "STAGE_NEWER_STATE", stage: newerStage }); continue; }
    const preselected = PROMOTABLE.has(cell.state) && !sameAsProd;
    listed.push({ packageId: pkg.packageId, state: cell.state, sameAsProd, preselected });
    if (preselected) packageIds.push(pkg.packageId);
  }
  return { packageIds, excluded, listed };
}

/**
 * Zusatzziele der Zielstufe, für die der Stand der Quellstufe keine eigenen Werte trägt (Warnung im Antrag, keine Sperre).
 * Liest `cells[sourceStage].nextValuesMissing` des Servers; `targetIds` (optional) schränkt auf die gewählten Ziele ein.
 * Ergebnis: [{ targetId, packageIds }] in der Reihenfolge der Ziele der Zielstufe.
 */
export function valuesMissingTargets(board, { sourceStage, targetStage, packageIds, targetIds } = {}) {
  const ids = new Set(packageIds || []);
  const byTarget = new Map();
  for (const pkg of board?.packages || []) {
    if (!ids.has(pkg.packageId)) continue;
    for (const targetId of pkg.cells?.[sourceStage]?.nextValuesMissing || []) {
      if (Array.isArray(targetIds) && !targetIds.includes(targetId)) continue;
      byTarget.set(targetId, [...(byTarget.get(targetId) || []), pkg.packageId]);
    }
  }
  const order = (stageOf(board, targetStage)?.allTargets || stageOf(board, targetStage)?.targets || []).map(target => target.targetId);
  const rank = id => (order.includes(id) ? order.indexOf(id) : order.length);
  return [...byTarget].sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b)).map(([targetId, list]) => ({ targetId, packageIds: list }));
}
